import { useNavigate } from 'react-router-dom';
import { Settings, LogOut, ChevronsUpDown, Shield } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/hooks/useTenant';
import { SUPER_ADMIN_EMAILS } from '@/constants/admin';
import { useSidebar } from '@/components/ui/sidebar';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

function getInitials(name: string | null | undefined): string {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  if (parts.length === 1) {
    return parts[0].charAt(0).toUpperCase();
  }
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

export function AccountMenu() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const { user, signOut } = useAuth();
  const { profile } = useTenant();
  const navigate = useNavigate();

  const displayName = profile?.full_name || 'Usuário';
  const email = user?.email || '';
  const initials = getInitials(profile?.full_name);

  const handleSettings = () => {
    navigate('/configuracoes');
  };

  const handleSignOut = async () => {
    await signOut();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className={`flex w-full items-center rounded-md transition-colors duration-150 hover:bg-sidebar-accent ${collapsed ? 'justify-center p-1.5' : 'gap-2.5 px-2 py-1.5'}`}
        >
          <Avatar className="h-7 w-7 shrink-0">
            <AvatarFallback className="bg-sidebar-accent text-sidebar-accent-foreground text-[11px] font-medium">
              {initials}
            </AvatarFallback>
          </Avatar>
          {!collapsed && (
            <>
              <div className="flex flex-col items-start text-left flex-1 min-w-0">
                <span className="text-[13px] font-medium text-sidebar-accent-foreground truncate w-full">
                  {displayName}
                </span>
                <span className="text-[11px] text-sidebar-foreground/60 truncate w-full">
                  {email}
                </span>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-sidebar-foreground/40" />
            </>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <div className="px-2 py-1.5">
          <p className="text-sm font-medium">{displayName}</p>
          <p className="text-xs text-muted-foreground">{email}</p>
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSettings} className="cursor-pointer text-[13px]">
          <Settings className="mr-2 h-3.5 w-3.5" />
          Configurações
        </DropdownMenuItem>
        {user?.email && SUPER_ADMIN_EMAILS.includes(user.email) && (
          <DropdownMenuItem onClick={() => navigate('/a12o')} className="cursor-pointer text-[13px]">
            <Shield className="mr-2 h-3.5 w-3.5" />
            Painel Admin
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-[13px]">
          <LogOut className="mr-2 h-3.5 w-3.5" />
          Sair
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
